import jsQR from 'jsqr';

interface QRScannerOptions {
  onResult: (result: string) => void;
  onError?: (error: string) => void;
  onClose?: () => void;
}

export class QRScannerNative {
  private videoElement: HTMLVideoElement | null = null;
  private canvasElement: HTMLCanvasElement | null = null;
  private isScanning = false;
  private containerId = 'qr-scanner-container';
  private animationFrameId: number | null = null;
  private stream: MediaStream | null = null;
  private zoomLevel = 1.0;
  private invertImage = false;
  private blackAndWhite = true;

  constructor(private options: QRScannerOptions) {}

  async start(): Promise<void> {
    try {
      console.log('🔧 QR Scanner: Starting native scanner...');
      this.createContainer();
      this.videoElement = document.querySelector(
        `#${this.containerId} video`,
      ) as HTMLVideoElement;
      if (!this.videoElement) {
        throw new Error('Video element not found');
      }

      this.canvasElement = document.createElement('canvas');
      const ctx = this.canvasElement.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas context not available');
      }

      // Camera permission should already be granted from page load
      // Just enumerate cameras and select the best one
      const cameras = await this.getAvailableCameras();
      const selectedCamera = this.selectBestCamera(cameras);
      console.log('🔧 QR Scanner: Selected camera:', selectedCamera);

      // Check if we're on iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      // Start camera with selected device
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          // On iOS, use facingMode instead of deviceId since labels are empty
          ...(isIOS
            ? { facingMode: 'environment' } // Force back camera on iOS
            : { deviceId: selectedCamera.deviceId }),
          // Optimized for QR scanning - smaller, focused area
          width: { min: 640, ideal: 1280, max: 1920 },
          height: { min: 480, ideal: 720, max: 1080 },
          // Higher frame rate for better detection
          frameRate: { min: 15, ideal: 30, max: 30 },
          // Focus on center area for QR codes
          aspectRatio: { ideal: 4 / 3 },
        },
      });

      this.videoElement.srcObject = this.stream;
      await this.videoElement.play();

      this.isScanning = true;
      this.scanLoop(ctx).catch((error) => {
        console.error('🔧 QR Scanner: Error in scan loop:', error);
      });

      console.log('🔧 QR Scanner: Native scanner started successfully');
    } catch (error) {
      console.error('Failed to start native QR scanner:', error);
      this.options.onError?.(
        error instanceof Error ? error.message : 'Failed to start scanner',
      );
    }
  }

  async stop(): Promise<void> {
    console.log('Stopping native QRScanner');
    this.isScanning = false;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => {
        track.stop();
        console.log('🔧 QR Scanner: Stopped track:', track.kind);
      });
      this.stream = null;
    }

    // Clear canvas to free memory
    if (this.canvasElement) {
      const ctx = this.canvasElement.getContext('2d');
      if (ctx) {
        ctx.clearRect(
          0,
          0,
          this.canvasElement.width,
          this.canvasElement.height,
        );
      }
      this.canvasElement = null;
    }

    // Reset video element
    if (this.videoElement) {
      this.videoElement.srcObject = null;
      this.videoElement = null;
    }

    console.log('Native QR Scanner stopped');
  }

  private async getAvailableCameras(): Promise<MediaDeviceInfo[]> {
    console.log('🔧 QR Scanner: Starting camera enumeration...');

    const devices = await navigator.mediaDevices.enumerateDevices();
    console.log(
      '🔧 QR Scanner: All devices found:',
      devices.map((d) => ({
        kind: d.kind,
        deviceId: d.deviceId,
        label: d.label,
        groupId: d.groupId,
      })),
    );

    const videoDevices = devices.filter(
      (device) => device.kind === 'videoinput',
    );

    console.log(
      '🔧 QR Scanner: Video devices found:',
      videoDevices.map((d) => ({
        deviceId: d.deviceId,
        label: d.label,
        groupId: d.groupId,
        hasLabel: !!d.label,
        labelLength: d.label ? d.label.length : 0,
      })),
    );

    return videoDevices;
  }

  private selectBestCamera(cameras: MediaDeviceInfo[]): MediaDeviceInfo {
    console.log(
      '🔧 QR Scanner: Available cameras:',
      cameras.map((c) => ({
        deviceId: c.deviceId,
        label: c.label,
        groupId: c.groupId,
      })),
    );

    // On iOS, just use the first camera (it's usually the back camera)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      console.log('🔧 QR Scanner: iOS detected, using first camera');
      return cameras[0];
    }

    // For other platforms, try to find back camera by label
    const backCamera = cameras.find((camera) => {
      const label = camera.label.toLowerCase();
      return (
        label.includes('back') ||
        label.includes('rear') ||
        label.includes('environment') ||
        label.includes('outdoor') ||
        label.includes('world')
      );
    });

    if (backCamera) {
      console.log('🔧 QR Scanner: Selected back camera:', backCamera.label);
      return backCamera;
    }

    // If no back camera found, try to find a camera that's NOT "front", "user", or "selfie"
    const nonFrontCamera = cameras.find((camera) => {
      const label = camera.label.toLowerCase();
      return (
        !label.includes('front') &&
        !label.includes('user') &&
        !label.includes('selfie') &&
        !label.includes('face')
      );
    });

    if (nonFrontCamera) {
      console.log(
        '🔧 QR Scanner: Selected non-front camera:',
        nonFrontCamera.label,
      );
      return nonFrontCamera;
    }

    // Last resort: use the first camera
    console.log(
      '🔧 QR Scanner: Using first available camera:',
      cameras[0].label,
    );
    return cameras[0];
  }

  private async scanLoop(ctx: CanvasRenderingContext2D): Promise<void> {
    if (!this.isScanning || !this.videoElement || !this.canvasElement) {
      return;
    }

    // Set canvas size to match video
    this.canvasElement.width = this.videoElement.videoWidth;
    this.canvasElement.height = this.videoElement.videoHeight;

    // Draw video frame to canvas
    ctx.drawImage(
      this.videoElement,
      0,
      0,
      this.canvasElement.width,
      this.canvasElement.height,
    );

    // Focus on center area for QR code detection
    const centerX = this.canvasElement.width / 2;
    const centerY = this.canvasElement.height / 2;
    const scanSize =
      Math.min(this.canvasElement.width, this.canvasElement.height) * 0.6; // 60% of smaller dimension

    const scanX = Math.max(0, centerX - scanSize / 2);
    const scanY = Math.max(0, centerY - scanSize / 2);
    const scanWidth = Math.min(scanSize, this.canvasElement.width - scanX);
    const scanHeight = Math.min(scanSize, this.canvasElement.height - scanY);

    // Get image data from center area only
    const imageData = ctx.getImageData(scanX, scanY, scanWidth, scanHeight);

    // Process image data (black/white conversion and inversion)
    const processedImageData = this.processImageData(imageData);

    // Try to detect QR code with processed image
    const code = jsQR(
      processedImageData.data,
      processedImageData.width,
      processedImageData.height,
      {
        inversionAttempts: 'attemptBoth',
      },
    );

    if (code) {
      console.log('🔧 QR Scanner: QR code detected:', code.data);
      await this.handleSuccess(code.data);
      return;
    }

    // Continue scanning
    this.animationFrameId = requestAnimationFrame(() => {
      this.scanLoop(ctx).catch((error) => {
        console.error('🔧 QR Scanner: Error in scan loop:', error);
      });
    });
  }

  private createContainer(): void {
    // Remove existing container if it exists
    const existingContainer = document.getElementById(this.containerId);
    if (existingContainer) {
      existingContainer.remove();
    }

    const container = document.createElement('div');
    container.id = this.containerId;

    // Simple container styling
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.zIndex = '10000';
    container.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.alignItems = 'center';
    container.style.justifyContent = 'center';
    // Prevent container from being maximized
    container.style.overflow = 'hidden';
    container.style.pointerEvents = 'auto';
    container.style.userSelect = 'none';
    container.style.webkitUserSelect = 'none';

    // Add instruction text
    const instruction = document.createElement('div');
    instruction.textContent =
      'Point your camera at a QR code. Use zoom to get closer view of small QR codes.';
    instruction.style.color = 'white';
    instruction.style.fontSize = '18px';
    instruction.style.marginBottom = '20px';
    instruction.style.textAlign = 'center';
    instruction.style.padding = '0 20px';
    container.appendChild(instruction);

    // Add video element
    const video = document.createElement('video');
    video.style.width = '100%';
    video.style.maxWidth = '500px';
    video.style.height = 'auto';
    video.style.border = '2px solid white';
    video.style.borderRadius = '10px';
    // Critical: Prevent native video controls on iOS
    video.setAttribute('playsinline', 'true');
    video.setAttribute('muted', 'true');
    video.setAttribute('autoplay', 'true');
    video.setAttribute('disablePictureInPicture', 'true');
    video.setAttribute('disableRemotePlayback', 'true');
    // Prevent video from maximizing
    video.style.objectFit = 'contain';
    video.style.maxHeight = '60vh';
    video.style.pointerEvents = 'none';
    container.appendChild(video);

    // Add scan area indicator
    const scanIndicator = document.createElement('div');
    scanIndicator.style.position = 'absolute';
    scanIndicator.style.top = '50%';
    scanIndicator.style.left = '50%';
    scanIndicator.style.transform = 'translate(-50%, -50%)';
    scanIndicator.style.width = '200px';
    scanIndicator.style.height = '200px';
    scanIndicator.style.border = '2px solid #00ff00';
    scanIndicator.style.borderRadius = '10px';
    scanIndicator.style.pointerEvents = 'none';
    scanIndicator.style.zIndex = '10001';
    container.appendChild(scanIndicator);

    // Add controls container
    const controlsContainer = document.createElement('div');
    controlsContainer.style.display = 'flex';
    controlsContainer.style.flexDirection = 'column';
    controlsContainer.style.alignItems = 'center';
    controlsContainer.style.gap = '15px';
    controlsContainer.style.marginTop = '20px';
    controlsContainer.style.padding = '0 20px';

    // Add zoom control
    const zoomContainer = document.createElement('div');
    zoomContainer.style.display = 'flex';
    zoomContainer.style.alignItems = 'center';
    zoomContainer.style.gap = '10px';
    zoomContainer.style.color = 'white';

    const zoomLabel = document.createElement('label');
    zoomLabel.textContent = 'Zoom:';
    zoomLabel.style.fontSize = '14px';
    zoomContainer.appendChild(zoomLabel);

    const zoomSlider = document.createElement('input');
    zoomSlider.type = 'range';
    zoomSlider.min = '1';
    zoomSlider.max = '8';
    zoomSlider.step = '0.1';
    zoomSlider.value = '3';
    zoomSlider.style.width = '150px';
    zoomSlider.oninput = (e) => {
      this.zoomLevel = parseFloat((e.target as HTMLInputElement).value);
      this.updateCameraZoom();
    };
    zoomContainer.appendChild(zoomSlider);

    const zoomValue = document.createElement('span');
    zoomValue.textContent = '1.0x';
    zoomValue.style.fontSize = '12px';
    zoomValue.style.minWidth = '30px';
    zoomContainer.appendChild(zoomValue);

    zoomSlider.oninput = (e) => {
      this.zoomLevel = parseFloat((e.target as HTMLInputElement).value);
      zoomValue.textContent = `${this.zoomLevel.toFixed(1)}x`;
      this.updateCameraZoom();
    };

    controlsContainer.appendChild(zoomContainer);

    // Add processing controls
    const processingContainer = document.createElement('div');
    processingContainer.style.display = 'flex';
    processingContainer.style.alignItems = 'center';
    processingContainer.style.gap = '15px';
    processingContainer.style.color = 'white';

    // Black and white toggle
    const bwContainer = document.createElement('div');
    bwContainer.style.display = 'flex';
    bwContainer.style.alignItems = 'center';
    bwContainer.style.gap = '5px';

    const bwCheckbox = document.createElement('input');
    bwCheckbox.type = 'checkbox';
    bwCheckbox.checked = this.blackAndWhite;
    bwCheckbox.onchange = (e) => {
      this.blackAndWhite = (e.target as HTMLInputElement).checked;
    };
    bwContainer.appendChild(bwCheckbox);

    const bwLabel = document.createElement('label');
    bwLabel.textContent = 'B&W';
    bwLabel.style.fontSize = '14px';
    bwContainer.appendChild(bwLabel);

    processingContainer.appendChild(bwContainer);

    // Invert toggle
    const invertContainer = document.createElement('div');
    invertContainer.style.display = 'flex';
    invertContainer.style.alignItems = 'center';
    invertContainer.style.gap = '5px';

    const invertCheckbox = document.createElement('input');
    invertCheckbox.type = 'checkbox';
    invertCheckbox.checked = this.invertImage;
    invertCheckbox.onchange = (e) => {
      this.invertImage = (e.target as HTMLInputElement).checked;
    };
    invertContainer.appendChild(invertCheckbox);

    const invertLabel = document.createElement('label');
    invertLabel.textContent = 'Invert';
    invertLabel.style.fontSize = '14px';
    invertContainer.appendChild(invertLabel);

    processingContainer.appendChild(invertContainer);

    controlsContainer.appendChild(processingContainer);

    container.appendChild(controlsContainer);

    // Add cancel button
    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.position = 'absolute';
    cancelButton.style.top = '20px';
    cancelButton.style.right = '20px';
    cancelButton.style.padding = '10px 20px';
    cancelButton.style.backgroundColor = '#ff4444';
    cancelButton.style.color = 'white';
    cancelButton.style.border = 'none';
    cancelButton.style.borderRadius = '5px';
    cancelButton.style.cursor = 'pointer';
    cancelButton.style.fontSize = '16px';
    cancelButton.onclick = () => this.close();
    container.appendChild(cancelButton);

    // Find the anchor element or use body
    const parent =
      document.getElementById('qr-scanner-anchor') || document.body;
    parent.appendChild(container);
  }

  private async handleSuccess(decodedText: string): Promise<void> {
    console.log('🔧 QR Scanner: Processing QR code result:', decodedText);

    // Validate the decoded text
    if (!decodedText || decodedText.trim().length === 0) {
      console.log(
        '🔧 QR Scanner: Empty or invalid QR code result, continuing scan',
      );
      return;
    }

    // Check if the decoded text looks like a valid QR code result
    // This could be a UUID, URL, or other expected format
    if (decodedText.length < 10) {
      console.log('🔧 QR Scanner: QR code result too short, continuing scan');
      return;
    }

    console.log('🔧 QR Scanner: Valid QR code detected, stopping scanner');
    await this.stop();
    this.options.onResult(decodedText);
    await this.close();
  }

  public async close(): Promise<void> {
    await this.stop();

    // Remove container
    const container = document.getElementById(this.containerId);
    if (container) {
      container.remove();
    }

    // Call close callback
    this.options.onClose?.();
  }

  private updateCameraZoom(): void {
    if (this.stream && this.videoElement) {
      const videoTrack = this.stream.getVideoTracks()[0];
      if (videoTrack && videoTrack.getCapabilities) {
        const capabilities = videoTrack.getCapabilities();
        // Check if zoom is supported (using type assertion)
        const zoomCapability = (capabilities as any).zoom;
        if (zoomCapability) {
          const newZoom = Math.min(
            Math.max(this.zoomLevel, zoomCapability.min || 1),
            zoomCapability.max || 8,
          );

          videoTrack
            .applyConstraints({
              advanced: [{ zoom: newZoom }] as any,
            })
            .catch((error) => {
              console.log(
                '🔧 QR Scanner: Zoom not supported on this device:',
                error,
              );
            });
        }
      }
    }
  }

  private processImageData(imageData: ImageData): ImageData {
    const data = imageData.data;
    const processedData = new Uint8ClampedArray(data.length);

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      if (this.blackAndWhite) {
        // Convert to black and white
        const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
        const bwValue = gray > 128 ? 255 : 0;

        if (this.invertImage) {
          processedData[i] = 255 - bwValue; // R
          processedData[i + 1] = 255 - bwValue; // G
          processedData[i + 2] = 255 - bwValue; // B
        } else {
          processedData[i] = bwValue; // R
          processedData[i + 1] = bwValue; // G
          processedData[i + 2] = bwValue; // B
        }
      } else {
        // Keep color but apply inversion if requested
        if (this.invertImage) {
          processedData[i] = 255 - r; // R
          processedData[i + 1] = 255 - g; // G
          processedData[i + 2] = 255 - b; // B
        } else {
          processedData[i] = r; // R
          processedData[i + 1] = g; // G
          processedData[i + 2] = b; // B
        }
      }

      processedData[i + 3] = a; // Alpha
    }

    return new ImageData(processedData, imageData.width, imageData.height);
  }

  private async ensureCameraPermission(): Promise<void> {
    try {
      // Request camera permission explicitly
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop the temporary stream immediately
      stream.getTracks().forEach((track) => track.stop());
      console.log('🔧 QR Scanner: Camera permission granted');

      // Wait a moment for camera enumeration to update
      await new Promise((resolve) => setTimeout(resolve, 100));
    } catch (error) {
      console.error('🔧 QR Scanner: Camera permission denied:', error);
      throw new Error('Camera permission denied');
    }
  }
}

// Legacy compatibility function
export const startQRScanner = async (
  onResult: (result: string) => void,
  onError?: (error: string) => void,
  onClose?: () => void,
): Promise<QRScannerNative> => {
  const scanner = new QRScannerNative({ onResult, onError, onClose });
  await scanner.start();
  return scanner;
};
